import { useEffect } from "react";
import { mustGetElementById, mustGetElementByName, mustQuerySelectorAtIndex } from "./helpers";
import { useProxyErrors } from "./useProxyErrors";
import { useProxyError } from "./useProxyError";
import { useProxyAction } from "./useProxyAction";

const getEmailInput = () => mustGetElementById<HTMLInputElement>('email');
const getVerificationCodeInput = () => mustGetElementById<HTMLInputElement>('email_ver_input');
const getNewPasswordInput = () => mustGetElementById<HTMLInputElement>('newPassword');
const getReenterPasswordInput = () => mustGetElementById<HTMLInputElement>('reenterPassword');

const getSendVerificationCodeButton = () => mustGetElementById<HTMLInputElement>('email_ver_but_send');
const getVerifyCodeButton = () => mustGetElementById<HTMLInputElement>('email_ver_but_verify');
const getSendNewCodeButton = () => mustGetElementById<HTMLInputElement>('email_ver_but_resend');
const getChangeEmailButton = () => mustGetElementById<HTMLInputElement>('email_ver_but_edit');
const getCreateButton = () => mustGetElementById<HTMLButtonElement>('continue');
const getCancelButton = () => mustGetElementById<HTMLButtonElement>('cancel');

const getTOUCheckBox = () => mustGetElementByName<HTMLInputElement>('agreedToTermsOfService');

export enum ItemLevelErrorIndexes {
  Email = 0,
  NewPassword = 1,
  ReenterPassword = 2,
}
const getEmailError = () =>
  mustQuerySelectorAtIndex('#api .error.itemLevel', ItemLevelErrorIndexes.Email, 'email error');
const getNewPasswordError = () =>
  mustQuerySelectorAtIndex('#api .error.itemLevel', ItemLevelErrorIndexes.NewPassword, 'new password error');
const getReenterPasswordError = () =>
  mustQuerySelectorAtIndex('#api .error.itemLevel', ItemLevelErrorIndexes.ReenterPassword, 'reenter password error');
const getPageLevelErrors = () => Array.from<HTMLElement>(document.querySelectorAll('#api .error.pageLevel'));
const getVerificationErrors = () => Array.from<HTMLElement>(document.querySelectorAll('#api .verificationErrorText'));

export enum PageLevelErrorIndexes {
  PasswordFieldsMismatch = 0,
  RequiredFieldMissing = 1,
  OneOrMoreIncorrectFields = 2,
  ServerError = 3,
}
export enum VerificationErrorIndexes {
  IncorrectCode = 0,
  TooManyAttempts = 1,
  TooManyRequests = 2,
  ExpiredCode = 3,
  TroubleVerifyingEmail = 4,
  IncorrectFormat = 5,
}

export function useSignUpProxy({ email, verificationCode, newPassword, reenterPassword, isTOUed } : {
  email?: string
  verificationCode?: string
  newPassword?: string
  reenterPassword?: string
  isTOUed?: boolean
}) {
  // fields
  useEffect(() => {
    if (email === undefined) return;
    getEmailInput().value = email;
  }, [email]);

  useEffect(() => {
    if (verificationCode === undefined) return;
    getVerificationCodeInput().value = verificationCode;
  }, [verificationCode]);

  useEffect(() => {
    if (newPassword === undefined) return;
    getNewPasswordInput().value = newPassword;
  }, [newPassword]);

  useEffect(() => {
    if (reenterPassword === undefined) return;
    getReenterPasswordInput().value = reenterPassword;
  }, [reenterPassword]);

  useEffect(() => {
    if (isTOUed === undefined) return;
    getTOUCheckBox().checked = isTOUed;
  }, [isTOUed]);

  // actions
  const sendCodeAction = useProxyAction(getSendVerificationCodeButton);
  const verifyCodeAction = useProxyAction(getVerifyCodeButton);
  const sendNewCodeAction = useProxyAction(getSendNewCodeButton);
  const changeEmailAction = useProxyAction(getChangeEmailButton);
  const createAction = useProxyAction(getCreateButton);
  const cancelAction = useProxyAction(getCancelButton);

  // errors
  const emailError = useProxyError(getEmailError);
  const newPasswordError = useProxyError(getNewPasswordError);
  const reenterPasswordError = useProxyError(getReenterPasswordError);
  const pageLevelError = useProxyErrors(getPageLevelErrors);
  const verificationError = useProxyErrors(getVerificationErrors);

  return {
    sendCodeAction, verifyCodeAction, sendNewCodeAction, changeEmailAction, createAction, cancelAction,
    verificationError, pageLevelError, emailError, newPasswordError, reenterPasswordError,
  };
}
